import { includes, filter, find, path, propEq } from 'ramda';
import { createSelector } from 'reselect';

/**
 * Paths to specific properties in an ApiUser object.
 */
const companyPath = ['api', 'company', 'data'];
const defaultCompanyIdPath = ['relationships', 'default-company', 'data', 'id'];
const asAdministratorPath = ['relationships', 'as-administrator', 'data'];
const asDriverPath = ['relationships', 'as-driver', 'data'];
const asSubcontractorPath = ['relationships', 'as-subcontractor', 'data'];
const asASLPath = ['relationships', 'as-asl', 'data'];

/**
 * Filters a list of companies based on the given IDs.
 */
const filterCompanies = (companies, companyIds) => {
  if (!companies || !companyIds) return [];

  return filter(company => includes(company.id, companyIds))(companies);
};

/**
 * Finds a company based on the given ID.
 */
const findCompany = (companies, companyId) => {
  if (!companies || !companyId) return undefined;

  return find(propEq('id', companyId))(companies);
};

/**
 * Selectors for unfiltered API data.
 */
export const selectCompanies = state => path(companyPath, state) || [];

/**
 * Selectors for linked company IDs by role.
 */
export const selectAdministratorCompanyIds = (state, ownProps) => (path(asAdministratorPath, ownProps.user) || []).map(c => c.id);
export const selectDriverCompanyIds = (state, ownProps) => (path(asDriverPath, ownProps.user) || []).map(c => c.id);
export const selectSubcontractorCompanyIds = (state, ownProps) => (path(asSubcontractorPath, ownProps.user) || []).map(c => c.id);
export const selectASLCompanyIds = (state, ownProps) => (path(asASLPath, ownProps.user) || []).map(c => c.id);

/**
 * Memoizing selectors for linked companies by role.
 */
export const getAdministratorCompanies = createSelector([selectCompanies, selectAdministratorCompanyIds], filterCompanies);
export const getDriverCompanies = createSelector([selectCompanies, selectDriverCompanyIds], filterCompanies);
export const getSubcontractorCompanies = createSelector([selectCompanies, selectSubcontractorCompanyIds], filterCompanies);
export const getASLCompanies = createSelector([selectCompanies, selectASLCompanyIds], filterCompanies);

/**
 * Sharable memoizing selectors for linked companies by role.
 */
export const makeGetAdministratorCompanies = () => createSelector([selectCompanies, selectAdministratorCompanyIds], filterCompanies);
export const makeGetDriverCompanies = () => createSelector([selectCompanies, selectDriverCompanyIds], filterCompanies);
export const makeGetSubcontractorCompanies = () => createSelector([selectCompanies, selectSubcontractorCompanyIds], filterCompanies);
export const makeGetASLCompanies = () => createSelector([selectCompanies, selectASLCompanyIds], filterCompanies);

/**
 * Selector for the default company ID.
 */
export const selectDefaultCompanyId = (state, ownProps) => path(defaultCompanyIdPath, ownProps.user);

/**
 * Memoizing selector for the default company.
 */
export const getDefaultCompany = createSelector([selectCompanies, selectDefaultCompanyId], findCompany);

/**
 * Sharable memoizing selector for the default company.
 */
export const makeGetDefaultCompany = () => createSelector([selectCompanies, selectDefaultCompanyId], findCompany);
